// src/utils/subjectMatch.js
//
// Shared, dependency-free subject matching. Used to relate a session's
// subject/session-type to a plan's subjects, and to other sessions' subjects.
//
// Matching is token-based: strings are lower-cased, split into meaningful word
// tokens, and test names / filler words are dropped. That makes "SAT Math",
// "ACT Math", and "Math" match; likewise "Reading", "Reading & Writing", and
// "SAT Reading & Writing"; and custom subjects like "Algebra 2" match
// "Algebra". Unrelated subjects ("Chemistry" vs "Physics") never match.

const STOP_TOKENS = new Set([
  "sat", "act", "psat", "ap",
  "and", "or", "the", "a", "an", "of", "to", "for", "with",
  "test", "prep", "tutoring", "session", "class", "course", "review", "practice",
  "part", "section",
  "i", "ii", "iii", "iv", "1", "2", "3", "4",
]);

/** Meaningful word tokens for a subject-ish string. */
export function subjectTokens(raw) {
  return String(raw || "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t && !STOP_TOKENS.has(t));
}

/** Order-independent comparable key for a subject-ish string ("" when empty). */
export function subjectMatchKey(raw) {
  return subjectTokens(raw).sort().join(" ");
}

/**
 * True when two subject-ish strings (session subject, session type, plan
 * subject, ...) refer to the same subject. Blank input never matches.
 */
export function sameSubject(a, b) {
  const ta = subjectTokens(a);
  const tb = subjectTokens(b);
  if (!ta.length || !tb.length) return false;
  if (subjectMatchKey(a) === subjectMatchKey(b)) return true;
  const set = new Set(ta);
  return tb.some((t) => set.has(t));
}

/**
 * Returns "SAT" | "ACT" | null — set when the string is a bare test name with
 * no subject detail ("SAT", "PSAT" → "SAT"; "ACT" → "ACT").
 */
export function bareTestName(raw) {
  const t = String(raw || "").trim().toLowerCase();
  if (t === "sat" || t === "psat") return "SAT";
  if (t === "act") return "ACT";
  return null;
}

/**
 * The test named anywhere in a subject string ("SAT Math" → "SAT",
 * "ACT English" → "ACT", "Math" → null). Matches whole words only, so
 * "Acting" is not an ACT reference.
 */
export function testNameIn(raw) {
  const tokens = String(raw || "")
    .toLowerCase()
    .split(/[^a-z0-9]+/);
  if (tokens.includes("act")) return "ACT";
  if (tokens.includes("sat") || tokens.includes("psat")) return "SAT";
  return null;
}
